import { Dictionary } from 'lodash';
import moment, { duration, Moment } from 'moment';
import { useCallback } from 'react';
import { Role, Team, User } from 'api/models';
import { getCalendars } from 'components/organisms/resource-management/scheduler-config/calendars';
import {
  AllocationEventModel,
  GroupByProjectResourceModel,
} from 'components/organisms/resource-management/scheduler-config/models';
import { getProjectsSorter } from 'components/organisms/resource-management/sorters';
import { ProjectsWithMembers } from 'components/organisms/resource-management/types';
import { getProjectsWithTeamMembersGroupedByRole } from 'components/organisms/resource-management/utils/associated-data';
import { concat } from 'components/organisms/resource-management/utils/common';
import { convertProjectTimelineAndPhasesToEvents } from 'components/organisms/resource-management/utils/events-conversion/project-timeline-phases';
import { convertProjectsToResourceTree } from 'components/organisms/resource-management/utils/resource-tree';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useGetAllAllocations } from 'data/templates/projects/hooks/use-get-all-allocations';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { hoursPerWeekModifier } from './hours-per-week-modifier';
import { useAllocationAsProjectEvents } from './project-events';

interface Props {
  roles: Role[];
  project: ProjectsWithMembers;
  teamMembership: Record<string, Team[]>;
  workingDaysConfig: WorkingDaysConfiguration;
  relativeDate: Moment;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  projectLookup: Dictionary<ProjectsWithMembers>;
  isHoursPerWeek?: boolean;
}
export const useProjectInfoProvider = ({
  project,
  teamMembership,
  workingDaysConfig,
  roles,
  relativeDate,
  userLookup,
  teamLookup,
  projectLookup,
  isHoursPerWeek,
}: Props): {
  getSchedulerProjectInfo: () => any;
  getMaximumDateForScheduler: () => Moment;
} => {
  const allAllocations = useGetAllAllocations();
  const isCollaborator = useIsCurrentUserCollaborator();
  const { convertAllocationsAsProjectEvents } = useAllocationAsProjectEvents({
    project,
    workingDaysConfig,
    relativeDate,
    teamLookup,
    userLookup,
    isHoursPerWeek,
  });

  const getResourceTree = useCallback(
    () =>
      convertProjectsToResourceTree(
        getProjectsWithTeamMembersGroupedByRole(
          [project],
          roles,
          teamMembership,
        ),
        {
          includeAssignUserOrPlaceholder: !isCollaborator,
          showTeams: !isCollaborator,
        },
      ),
    [isCollaborator, project, roles, teamMembership],
  );
  const getProjectEvents = useCallback(
    () =>
      convertProjectTimelineAndPhasesToEvents(
        [project],
        isHoursPerWeek,
        relativeDate,
      ),
    [project, isHoursPerWeek, relativeDate],
  );
  const getMaximumDateForScheduler = useCallback(() => {
    const maximumDuration = Math.max(
      ...Object.values(allAllocations).map((x) => x.startDay + x.duration - 1),
      duration(1, 'month').asDays() - duration(1, 'week').asDays(),
    );

    const maximumAllocatedDate = relativeDate
      .add(maximumDuration, 'day')
      .endOf('day');
    const maximumDateBetweenProjectAndAllocations = moment(
      project.dueDate,
    ).isAfter(maximumAllocatedDate)
      ? moment(project.dueDate)
      : maximumAllocatedDate;

    return maximumDateBetweenProjectAndAllocations.add(1, 'week');
  }, [allAllocations, project.dueDate, relativeDate]);
  const getSchedulerProjectInfo = useCallback(() => {
    let modifiedAllocations = Object.values(allAllocations);

    if (isHoursPerWeek) {
      modifiedAllocations = hoursPerWeekModifier(modifiedAllocations);
    }

    const allocationEvents =
      convertAllocationsAsProjectEvents(modifiedAllocations);

    return {
      resourceStore: {
        useRawData: true,
        data: getResourceTree(),
        modelClass: GroupByProjectResourceModel,
        sorters: getProjectsSorter(projectLookup),
        reapplySortersOnAdd: true,
      },
      eventStore: {
        useRawData: true,
        data: concat(getProjectEvents(), allocationEvents),
        modelClass: AllocationEventModel,
      },
      calendarManagerStore: {
        useRawData: true,
        data: getCalendars(workingDaysConfig),
      },
      calendar: 'nonWorkingDays',
    };
  }, [
    allAllocations,
    convertAllocationsAsProjectEvents,
    getProjectEvents,
    getResourceTree,
    projectLookup,
    workingDaysConfig,
    isHoursPerWeek,
  ]);

  return {
    getSchedulerProjectInfo,
    getMaximumDateForScheduler,
  };
};
